import { pick } from 'lodash'
import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { adUnitsAttributesMapper } from 'features/inventory/domain/components/ProductAdUnitsTable/utils'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { floatPercentToDTO } from 'utils/number'
import { Schema } from '../../schema'
import { UPDATE_DOMAIN_VIEWABILITY_TOOLS } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateDomainViewabilityTools } from './types'

export const useUpdateDomainViewabilityTools = (): UseUpdateDomainViewabilityTools => {
  const { t } = useTranslation('features/domain')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_DOMAIN_VIEWABILITY_TOOLS,
    onCompleted: async data => {
      const {
        updateDomainViewabilityTool: { errors },
      } = data

      setLoading(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['domain'],
  })

  const mapper = (variables: Schema): Variables => {
    const {
      adUnitsAttributes,
      gamAdRenderMargin,
      gamAdRequestMargin,
      kvTargetingAndPrebidAuction,
      trafficAffected,
    } = variables

    const fields: Variables = {
      ...pick(variables, [
        'adHoldingEnabled',
        'creativeCodeKeywords',
        'distanceFromPageHeaderDesktop',
        'distanceFromPageHeaderMobile',
        'domainId',
        'excludeCreativeTypesEnabled',
        'extraSpaceBelowAdDesktop',
        'extraSpaceBelowAdMobile',
        'lazyLoadingEnabled',
        'lazyLoadTargetingPrebidAuctionEnabled',
        'lazyLoadType',
        'mobileScaling',
        'scrollSlowingEnabled',
        'scrollSlowingIntensity',
        'viewabilityToolsEnabled',
      ]),
      adUnitsAttributes: adUnitsAttributesMapper<'viewabilityToolsEnabled'>(
        adUnitsAttributes,
        'viewabilityToolsEnabled',
      ),
      gamAdRenderMargin: floatPercentToDTO(gamAdRenderMargin),
      gamAdRequestMargin: floatPercentToDTO(gamAdRequestMargin),
      kvTargetingAndPrebidAuction: floatPercentToDTO(kvTargetingAndPrebidAuction),
      trafficAffected: floatPercentToDTO(trafficAffected),
    }

    return fields
  }

  const update = (variables: Schema): void => {
    setLoading(true)

    mutateAsync({
      input: mapper(variables),
    })
  }

  return { errors, update }
}
